/**
 * Type-safe field update utilities for complex objects
 */

import type {
  SiteDetails,
  SiteChanges,
  SiteCoordinates,
} from '@/features/sites/types';

/**
 * Maps SiteDetails fields to their corresponding SiteChanges structure
 * This provides type safety when converting field updates to API payload
 */
type SiteFieldUpdateMapping = {
  siteName: (value: string) => Pick<SiteChanges, 'siteName'>;
  priority: (value: number) => Pick<SiteChanges, 'priority'>;
  currentState: (value: string) => Pick<SiteChanges, 'currentState'>;
  description: (value: string) => Pick<SiteChanges, 'description'>;
  requiredChanges: (value: string) => Pick<SiteChanges, 'requiredChanges'>;
  additionalWorks: (value: string) => Pick<SiteChanges, 'additionalWorks'>;
  coordinates: (
    value: SiteCoordinates
  ) => Pick<SiteChanges, 'latitude' | 'longitude'>;
};

/**
 * Updatable fields from SiteDetails (excludes read-only fields)
 */
type UpdatableSiteField = keyof SiteFieldUpdateMapping;

/**
 * Type-safe field value extractor
 */
type SiteFieldValue<K extends UpdatableSiteField> = K extends 'coordinates'
  ? SiteCoordinates
  : K extends keyof SiteDetails
    ? SiteDetails[K]
    : never;

/**
 * Creates type-safe update data for site field updates
 */
export function createSiteFieldUpdate<K extends UpdatableSiteField>(
  field: K,
  value: SiteFieldValue<K>
): Partial<SiteChanges> {
  const updateMapping: SiteFieldUpdateMapping = {
    siteName: (val: string) => ({ siteName: val }),
    priority: (val: number) => ({ priority: val }),
    currentState: (val: string) => ({ currentState: val }),
    description: (val: string) => ({ description: val }),
    requiredChanges: (val: string) => ({ requiredChanges: val }),
    additionalWorks: (val: string) => ({ additionalWorks: val }),
    coordinates: (val: SiteCoordinates) => ({
      latitude: val.latitude,
      longitude: val.longitude,
    }),
  };

  return updateMapping[field](value as never);
}

/**
 * Validates that a field is updatable (not read-only)
 */
export function isUpdatableField(
  field: keyof SiteDetails
): field is UpdatableSiteField {
  const readOnlyFields: Array<keyof SiteDetails> = [
    'id',
    'projectId',
    'createdAt',
    'updatedAt',
  ];

  return !readOnlyFields.includes(field);
}

/**
 * Type guard to validate field values at runtime
 */
export function validateFieldValue<K extends UpdatableSiteField>(
  field: K,
  value: unknown
): value is SiteFieldValue<K> {
  switch (field) {
    case 'siteName':
    case 'currentState':
    case 'description':
    case 'requiredChanges':
    case 'additionalWorks':
      return typeof value === 'string';

    case 'priority':
      return typeof value === 'number' && !isNaN(value);

    case 'coordinates':
      return (
        typeof value === 'object' &&
        value !== null &&
        'latitude' in value &&
        'longitude' in value &&
        typeof (value as SiteCoordinates).latitude === 'number' &&
        typeof (value as SiteCoordinates).longitude === 'number' &&
        !isNaN((value as SiteCoordinates).latitude) &&
        !isNaN((value as SiteCoordinates).longitude)
      );

    default:
      return false;
  }
}

/**
 * Export types for external use
 */
export type { UpdatableSiteField, SiteFieldValue };
